import { setUser as sentrySetUser, setTags as setSentryTags } from '@sentry/react';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import { useEffect, useRef } from 'react';
import { LAUNCH_DARKLY_CLIENT_SIDE_ID } from '@/config';
import { identifyTelemetry } from '@/api/telemetry';
import { getRegionConfig, useRegion } from '@/context/region';
import { readPersistedCliOnboardingSessionId } from '@/utils/cli-onboarding-identity';
import { useAuth } from './auth/hooks';
import { useCustomerIo } from './customer-io/hooks';
import { useSegment } from './segment/hooks';
import { useSnitcher } from './snitcher/hooks';

// Wrapper hook that safely handles missing LaunchDarkly provider
function useLDClientSafe() {
  // When LaunchDarkly is not configured, don't call useLDClient
  // This avoids the error from calling the hook outside of LDProvider
  if (!LAUNCH_DARKLY_CLIENT_SIDE_ID) {
    return undefined;
  }

  // eslint-disable-next-line react-hooks/rules-of-hooks
  return useLDClient();
}

export function IdentityProvider({ children }: { children: React.ReactNode }) {
  const ldClient = useLDClientSafe();
  const segment = useSegment();
  const customerIo = useCustomerIo();
  const snitcher = useSnitcher();
  const { currentUser, currentOrganization } = useAuth();
  const { selectedRegion } = useRegion();
  const hasIdentifiedOrg = useRef(false);
  const hasAliasedCliOnboarding = useRef(false);

  useEffect(() => {
    const cliOnboardingSessionId = readPersistedCliOnboardingSessionId();

    if (!cliOnboardingSessionId) return;

    segment.setAnonymousId(cliOnboardingSessionId);
  }, [segment]);

  useEffect(() => {
    if (!currentUser?._id || hasAliasedCliOnboarding.current) return;

    const cliOnboardingSessionId = readPersistedCliOnboardingSessionId();

    if (!cliOnboardingSessionId) return;

    hasAliasedCliOnboarding.current = true;
    segment.setAnonymousId(cliOnboardingSessionId);
    segment.alias(cliOnboardingSessionId, currentUser._id);
    void identifyTelemetry(cliOnboardingSessionId).catch(() => undefined);
  }, [currentUser?._id, segment]);

  useEffect(() => {
    if (!currentOrganization || !currentUser) return;

    const hasExternalId = currentUser._id;
    const hasOrganization = currentOrganization._id;
    const shouldMonitor = hasExternalId && hasOrganization;

    if (shouldMonitor) {
      if (!hasIdentifiedOrg.current) {
        segment.identify(currentUser);
        customerIo.identify(currentUser);
        snitcher.identify(currentUser, currentOrganization);

        sentrySetUser({
          email: currentUser.email ?? '',
          username: `${currentUser.firstName} ${currentUser.lastName}`,
          id: currentUser._id,
        });

        setSentryTags({
          'user.createdAt': currentUser.createdAt,
          'organization.id': currentOrganization._id,
          'organization.name': currentOrganization.name,
          'organization.tier': currentOrganization.apiServiceLevel,
          'organization.createdAt': currentOrganization.createdAt,
        });

        hasIdentifiedOrg.current = true;
      }

      if (ldClient) {
        const regionConfig = getRegionConfig(selectedRegion);
        const awsRegion = regionConfig?.awsRegion || '';

        ldClient.identify({
          kind: 'multi',
          organization: {
            key: currentOrganization._id,
            name: currentOrganization.name,
            createdAt: currentOrganization.createdAt,
            tier: currentOrganization.apiServiceLevel,
          },
          user: {
            key: currentUser._id,
            firstName: currentUser.firstName,
            lastName: currentUser.lastName,
            email: currentUser.email,
          },
          region: {
            key: awsRegion || 'unknown',
            awsRegion: awsRegion,
          },
        });
      }
    } else {
      sentrySetUser(null);
    }
  }, [ldClient, currentOrganization, currentUser, segment, customerIo, snitcher, selectedRegion]);

  return <>{children}</>;
}
